import { useEffect, useState } from "react";
import {
  adminCreatePhotograph,
  adminDeletePhotograph,
  adminListPhotographs,
  adminReplaceImage,
  adminUpdatePhotograph,
  imageUrl,
} from "../../lib/api";

function humanBytes(n) {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

export default function AdminPhotographs() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null); // photo id being edited
  const [busyId, setBusyId] = useState(null);

  // Upload form state
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [alt, setAlt] = useState("");
  const [customId, setCustomId] = useState("");
  const [published, setPublished] = useState(false);
  const [uploading, setUploading] = useState(false);

  async function refresh() {
    setLoading(true);
    setError("");
    try {
      setPhotos(await adminListPhotographs());
    } catch (e) {
      setError("Failed to load photographs");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { refresh(); }, []);

  async function onUpload(e) {
    e.preventDefault();
    if (!file) { setError("Choose a file"); return; }
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title);
      fd.append("alt", alt);
      fd.append("published", published ? "true" : "false");
      if (customId) fd.append("custom_id", customId);
      await adminCreatePhotograph(fd);
      setFile(null);
      setTitle(""); setAlt(""); setCustomId(""); setPublished(false);
      document.getElementById("admin-upload-file").value = "";
      await refresh();
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  async function toggleHero(p) {
    setBusyId(p.id);
    try {
      await adminUpdatePhotograph(p.id, { hero: !p.hero });
      await refresh();
    } finally { setBusyId(null); }
  }

  async function togglePublish(p) {
    setBusyId(p.id);
    try {
      await adminUpdatePhotograph(p.id, { published: !p.published });
      await refresh();
    } finally { setBusyId(null); }
  }

  async function onDelete(p) {
    const ok = window.confirm(
      `Delete photograph ${p.id}? This is irreversible. If it has any routes (questions leading to or from it), those will be removed too. Proceed?`
    );
    if (!ok) return;
    setBusyId(p.id);
    try {
      // First call without confirm to see if it has routes
      await adminDeletePhotograph(p.id, false);
      await refresh();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && (detail.incoming || detail.outgoing)) {
        const okCascade = window.confirm(
          `This photograph has ${detail.incoming} incoming and ${detail.outgoing} outgoing question route(s). Delete anyway and remove those routes?`
        );
        if (okCascade) {
          try {
            await adminDeletePhotograph(p.id, true);
            await refresh();
          } catch { setError("Delete failed"); }
        }
      } else {
        setError("Delete failed");
      }
    } finally { setBusyId(null); }
  }

  async function onReplace(p, ev) {
    const f = ev.target.files?.[0];
    if (!f) return;
    setBusyId(p.id);
    try {
      await adminReplaceImage(p.id, f);
      await refresh();
    } catch { setError("Replace failed"); }
    finally { setBusyId(null); ev.target.value = ""; }
  }

  return (
    <div data-testid="admin-photographs-page" className="space-y-10">
      <header>
        <h1 className="font-serif-display italic text-2xl md:text-3xl text-white/85">Photographs</h1>
        <p className="mt-1 text-sm text-white/50 font-serif-display italic">{photos.length} total</p>
      </header>

      {/* Upload form */}
      <section className="border border-white/10 rounded-md p-6 bg-black/20">
        <h2 className="font-serif-display italic text-lg text-white/85 mb-4">Upload a new photograph</h2>
        <form onSubmit={onUpload} data-testid="admin-upload-form" className="grid gap-4 md:grid-cols-2">
          <label className="block md:col-span-2">
            <span className="block text-xs uppercase tracking-[0.22em] text-white/45 mb-1 font-serif-display italic">Image file (original)</span>
            <input
              id="admin-upload-file"
              data-testid="admin-upload-file"
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/tiff"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="text-sm text-white/75 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border file:border-white/15 file:bg-transparent file:text-white/75 hover:file:bg-white/[0.04]"
            />
            <span className="block text-[11px] text-white/40 mt-2 font-serif-display italic">
              The original is stored privately. A web-optimised version (≤2000px long edge) is generated for public display.
            </span>
          </label>
          <label className="block">
            <span className="block text-xs uppercase tracking-[0.22em] text-white/45 mb-1 font-serif-display italic">Title</span>
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} className="q-input" data-testid="admin-upload-title" />
          </label>
          <label className="block">
            <span className="block text-xs uppercase tracking-[0.22em] text-white/45 mb-1 font-serif-display italic">Alt text (accessibility)</span>
            <input type="text" value={alt} onChange={(e) => setAlt(e.target.value)} className="q-input" data-testid="admin-upload-alt" />
          </label>
          <label className="block">
            <span className="block text-xs uppercase tracking-[0.22em] text-white/45 mb-1 font-serif-display italic">Custom id (optional)</span>
            <input type="text" placeholder="Auto: p40, p41…" value={customId} onChange={(e) => setCustomId(e.target.value)} className="q-input" data-testid="admin-upload-id" />
          </label>
          <label className="flex items-center gap-3 mt-2">
            <input type="checkbox" checked={published} onChange={(e) => setPublished(e.target.checked)} data-testid="admin-upload-published" />
            <span className="text-sm text-white/70 font-serif-display italic">Publish immediately</span>
          </label>
          <div className="md:col-span-2 flex items-center gap-4">
            <button type="submit" disabled={uploading || !file} className="btn-ghost disabled:opacity-40" data-testid="admin-upload-submit">
              {uploading ? "Uploading…" : "Upload photograph"}
            </button>
            {error && <span className="text-sm text-red-300/80 font-serif-display italic" data-testid="admin-photos-error">{error}</span>}
          </div>
        </form>
      </section>

      {/* List */}
      <section>
        <h2 className="font-serif-display italic text-lg text-white/85 mb-4">All photographs</h2>
        {loading && <p className="text-sm text-white/45 font-serif-display italic">Loading…</p>}
        <ul className="space-y-4">
          {photos.map((p) => (
            <PhotographRow
              key={p.id}
              photo={p}
              busy={busyId === p.id}
              onEditToggle={() => setEditing(editing === p.id ? null : p.id)}
              isEditing={editing === p.id}
              onSaved={() => { setEditing(null); refresh(); }}
              onTogglePublish={() => togglePublish(p)}
              onToggleHero={() => toggleHero(p)}
              onDelete={() => onDelete(p)}
              onReplace={(ev) => onReplace(p, ev)}
            />
          ))}
        </ul>
      </section>
    </div>
  );
}

function PhotographRow({ photo, busy, onEditToggle, isEditing, onSaved, onTogglePublish, onToggleHero, onDelete, onReplace }) {
  const [title, setTitle] = useState(photo.title || "");
  const [alt, setAlt] = useState(photo.alt || "");
  const [dateVal, setDateVal] = useState(photo.date || "");
  const [newId, setNewId] = useState(photo.id);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    setTitle(photo.title || "");
    setAlt(photo.alt || "");
    setDateVal(photo.date || "");
    setNewId(photo.id);
  }, [photo, isEditing]);

  async function save() {
    setSaving(true); setErr("");
    try {
      const patch = { title, alt, date: dateVal };
      if (newId && newId !== photo.id) patch.new_id = newId;
      await adminUpdatePhotograph(photo.id, patch);
      onSaved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Save failed");
    } finally { setSaving(false); }
  }

  return (
    <li data-testid={`admin-photo-row-${photo.id}`} className="border border-white/10 rounded-md p-4 flex gap-4 bg-black/20">
      <img
        src={imageUrl(photo.image)}
        alt={photo.alt || ""}
        className="w-24 h-24 object-cover flex-shrink-0 bg-black/40"
        loading="lazy"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-3">
          <span className="font-mono-editor text-xs text-white/45">{photo.id}</span>
          <span className="font-serif-display italic text-white/85 truncate">{photo.title || "Untitled"}</span>
          {photo.hero && (
            <span className="text-[10px] uppercase tracking-[0.22em] text-amber-200/85 border border-amber-200/30 px-1.5 py-0.5 rounded" data-testid={`admin-photo-hero-tag-${photo.id}`}>★ Landing</span>
          )}
          {!photo.published && (
            <span className="text-[10px] uppercase tracking-[0.22em] text-yellow-200/75 border border-yellow-200/25 px-1.5 py-0.5 rounded">Unpublished</span>
          )}
        </div>
        <p className="mt-1 text-xs text-white/45 truncate">{photo.alt || <em>no alt</em>}</p>
        {isEditing && (
          <div className="mt-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="block text-[10px] uppercase tracking-[0.22em] text-white/45 mb-1 font-serif-display italic">Internal id</span>
                <input type="text" value={newId} onChange={(e) => setNewId(e.target.value)} className="q-input text-base" data-testid={`admin-edit-id-${photo.id}`} />
              </label>
              <label className="block">
                <span className="block text-[10px] uppercase tracking-[0.22em] text-white/45 mb-1 font-serif-display italic">Date</span>
                <input type="text" value={dateVal} onChange={(e) => setDateVal(e.target.value)} className="q-input text-base" data-testid={`admin-edit-date-${photo.id}`} />
              </label>
            </div>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-[0.22em] text-white/45 mb-1 font-serif-display italic">Title</span>
              <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} className="q-input text-base" data-testid={`admin-edit-title-${photo.id}`} />
            </label>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-[0.22em] text-white/45 mb-1 font-serif-display italic">Alt text</span>
              <input type="text" value={alt} onChange={(e) => setAlt(e.target.value)} className="q-input text-base" data-testid={`admin-edit-alt-${photo.id}`} />
            </label>
            {err && <p className="text-xs text-red-300/80 font-serif-display italic">{err}</p>}
            <div className="flex items-center gap-3">
              <button type="button" onClick={save} disabled={saving} className="btn-ghost disabled:opacity-40" data-testid={`admin-edit-save-${photo.id}`}>
                {saving ? "Saving…" : "Save"}
              </button>
              <button type="button" onClick={onEditToggle} className="text-xs text-white/50 hover:text-white font-serif-display italic">Cancel</button>
            </div>
          </div>
        )}
      </div>
      <div className="flex flex-col gap-2 text-xs">
        <button type="button" onClick={onEditToggle} className="btn-ghost text-[10px]" data-testid={`admin-photo-edit-${photo.id}`}>
          {isEditing ? "Close" : "Edit"}
        </button>
        <button
          type="button"
          onClick={onToggleHero}
          disabled={busy}
          className={`btn-ghost text-[10px] ${photo.hero ? "!text-amber-200 !border-amber-200/40" : ""}`}
          data-testid={`admin-photo-hero-${photo.id}`}
          title={photo.hero ? "Currently the landing photograph" : "Set as landing photograph"}
        >
          {photo.hero ? "★ Landing" : "Set as landing"}
        </button>
        <button type="button" onClick={onTogglePublish} disabled={busy} className="btn-ghost text-[10px]" data-testid={`admin-photo-publish-${photo.id}`}>
          {photo.published ? "Unpublish" : "Publish"}
        </button>
        <label className="btn-ghost text-[10px] cursor-pointer">
          Replace
          <input type="file" accept="image/*" onChange={onReplace} className="hidden" data-testid={`admin-photo-replace-${photo.id}`} />
        </label>
        <button type="button" onClick={onDelete} disabled={busy} className="btn-ghost text-[10px] text-red-300/80 hover:!text-red-200" data-testid={`admin-photo-delete-${photo.id}`}>
          Delete
        </button>
      </div>
    </li>
  );
}
